<template>
  <div class="project-financial-overview bg-white p-6 rounded-lg border">
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      
      <!-- Column 1: Project Total Value -->
      <div class="flex flex-col justify-center">
        <div class="text-center">
          <h4 class="text-sm font-medium text-slate-600 mb-2">Project Total Value</h4>
          <div class="text-3xl font-bold text-slate-900">
            <span v-if="isLoadingBudget" class="animate-pulse text-slate-400">...</span>
            <span v-else>{{ formatProjectValue(projectTotal) }}</span>
          </div>
        </div>
      </div>

      <!-- Column 2: Revenue and Expenses Progress -->
      <div class="space-y-4">
        <!-- Total Revenue -->
        <div>
          <div class="flex justify-between items-center mb-2">
            <span class="text-sm font-medium text-slate-700">Total Revenue</span>
            <span class="text-sm text-slate-600">
              <span v-if="isLoadingFinancials" class="animate-pulse">...</span>
              <span v-else>{{ currency(totalRevenue) }}</span>
            </span>
          </div>
          <BaseProgressBar 
            :progress="revenueProgress" 
            variant="success"
            class="mb-1"
          />
          <div class="text-xs text-slate-500">
            {{ revenueProgress }}% of project value
          </div>
        </div>

        <!-- Total Expenses -->
        <div>
          <div class="flex justify-between items-center mb-2">
            <span class="text-sm font-medium text-slate-700">Total Expenses</span>
            <span class="text-sm text-slate-600">
              <span v-if="isLoadingFinancials" class="animate-pulse">...</span>
              <span v-else>{{ currency(totalExpenses) }}</span>
            </span>
          </div>
          <BaseProgressBar 
            :progress="expenseProgress" 
            variant="warning"
            class="mb-1"
          />
          <div class="text-xs text-slate-500">
            {{ expenseProgress }}% of project value
          </div>
        </div>
      </div>

      <!-- Column 3: Profitability Donut Chart -->
      <div class="flex flex-col justify-center">
        <div class="text-center">
          <h4 class="text-sm font-medium text-slate-600 mb-4">Profitability</h4>
          <div v-if="isLoadingFinancials" class="flex items-center justify-center h-32">
            <span class="i-heroicons-arrow-path text-slate-400 size-8 animate-spin" />
          </div>
          <div v-else class="relative">
            <ChartPercentGauge
              :value="[profitabilityPercentage]"
              :height="'160'"
              :options="{
                labels: ['Profit'],
                colors: profitabilityPercentage >= 0 ? ['#10B981'] : ['#EF4444'],
                plotOptions: {
                  radialBar: {
                    hollow: {
                      size: '60%'
                    },
                    dataLabels: {
                      name: {
                        fontSize: '12px',
                        color: '#6B7280',
                        offsetY: 20
                      },
                      value: {
                        fontSize: '24px',
                        fontWeight: '600',
                        color: profitabilityPercentage >= 0 ? '#059669' : '#DC2626',
                        offsetY: -10,
                        formatter: function(val) {
                          return val + '%'
                        }
                      }
                    }
                  }
                }
              }"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Financial Summary Row -->
    <div class="mt-6 pt-4 border-t border-slate-200">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
        <div>
          <div class="text-xs text-slate-600">Revenue</div>
          <div class="text-sm font-semibold text-green-600">
            <span v-if="isLoadingFinancials" class="animate-pulse">...</span>
            <span v-else>{{ currency(totalRevenue) }}</span>
          </div>
        </div>
        <div>
          <div class="text-xs text-slate-600">Expenses</div>
          <div class="text-sm font-semibold text-orange-600">
            <span v-if="isLoadingFinancials" class="animate-pulse">...</span>
            <span v-else>{{ currency(totalExpenses) }}</span>
          </div>
        </div>
        <div>
          <div class="text-xs text-slate-600">Profit/Loss</div>
          <div 
            :class="[
              'text-sm font-semibold',
              profitLoss >= 0 ? 'text-green-600' : 'text-red-600'
            ]"
          >
            <span v-if="isLoadingFinancials" class="animate-pulse">...</span>
            <span v-else>{{ currency(profitLoss) }}</span>
          </div>
        </div>
        <div>
          <div class="text-xs text-slate-600">Margin</div>
          <div 
            :class="[
              'text-sm font-semibold',
              profitabilityPercentage >= 0 ? 'text-green-600' : 'text-red-600'
            ]"
          >
            <span v-if="isLoadingFinancials" class="animate-pulse">...</span>
            <span v-else>{{ profitabilityPercentage.toFixed(1) }}%</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, PropType } from 'vue'
import BaseProgressBar from '@/components/base/BaseProgressBar.vue'
import ChartPercentGauge from '@/components/ChartPercentGauge.vue'
import { currency } from '@/utils'
import type { Budget } from '@/models/budget'

interface FinancialData {
  totalRevenue: number
  totalExpenses: number
  invoices?: any[]
  bills?: any[]
}

const props = defineProps({
  projectId: {
    type: String,
    required: true
  },
  budget: {
    type: Object as PropType<Budget | null>,
    default: null
  },
  financialData: {
    type: Object as PropType<FinancialData | null>,
    default: null
  },
  isLoadingBudget: {
    type: Boolean,
    default: false
  },
  isLoadingFinancials: {
    type: Boolean,
    default: false
  }
})

// Project total value from budget
const projectTotal = computed(() => {
  if (!props.budget) return 0
  return props.budget.getTotalPrice() || 0
})

// Financial calculations
const totalRevenue = computed(() => {
  if (!props.financialData) {
    return 0
  }
  
  const revenue = props.financialData.totalRevenue || 0
  return revenue
})

const totalExpenses = computed(() => {
  if (!props.financialData) {
    return 0
  }
  
  const expenses = props.financialData.totalExpenses || 0
  return expenses
})

const profitLoss = computed(() => {
  return totalRevenue.value - totalExpenses.value
})

// Progress calculations
const revenueProgress = computed(() => {
  if (projectTotal.value === 0) return 0
  return Math.min(Math.round((totalRevenue.value / projectTotal.value) * 100), 100)
})

const expenseProgress = computed(() => {
  if (projectTotal.value === 0) return 0
  return Math.min(Math.round((totalExpenses.value / projectTotal.value) * 100), 100)
})

// Profitability percentage for donut chart
const profitabilityPercentage = computed(() => {
  if (totalRevenue.value === 0) return 0
  return Math.round((profitLoss.value / totalRevenue.value) * 100)
})

// Format project value with fallback
const formatProjectValue = (value: number | null) => {
  if (value === null || value === undefined) return '--'
  return currency(value)
}
</script> 